import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero, Section, FeatureGrid, CTA } from "@/components/site/Sections";
import { ReviewsSection } from "@/components/site/Reviews";
import { CoveragePills } from "@/components/site/CoverageBanner";
import heroVideoAsset from "@/assets/hero-video.mp4.asset.json";
import photo37 from "@/assets/photo-37.jpg.asset.json";
import photo50 from "@/assets/photo-50.jpg.asset.json";
import photo53 from "@/assets/photo-53.jpg.asset.json";

const workItems = [
  {
    title: "Belysning i alle rum",
    body: "Loftslamper, LED-spots, udendørsarmaturer og designpendler — vi monterer, dæmper og tilslutter, så det ser ud og fungerer, som du ønsker.",
  },
  {
    title: "Stikkontakter & databytesikring",
    body: "Udskiftning af gamle stikkontakter, opsætning af nye dobbeltudtag, jordforbindelse og HPFI-relæer, der beskytter dig og din familie.",
  },
  {
    title: "Elbil & ladestandere",
    body: "Komplet installation af ladeboks fra ABB, Easee eller Zaptec. Vi tager os af tilslutning, sikring og styring, så du kan lade sikkert hjemme.",
  },
  {
    title: "Fejlfinding & akut hjælp",
    body: "Strømsvigt, kortslutning eller en HPFI der slår fra? Vi finder årsagen hurtigt, retter fejlen og dokumenterer arbejdet efter gældende standarder.",
  },
];

const TITLE = "Elektriker København & omegn | Autoriseret elektriker, Elektriker-Elektriker";
const DESCRIPTION =
  "Autoriseret elektriker i København og hele Storkøbenhavn. Hurtig udrykning, faste priser og garanti på alt arbejde. Ring +45 28 7000 28 eller indhent et uforpligtende tilbud.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: "https://elektriker-elektriker.dk/" },
    ],
  }),
  component: HomePage,
});

const services = [
  { title: "Akut elektriker", body: "Hurtig udrykning ved strømsvigt, kortslutning eller HPFI der slår fra." },
  { title: "Ladestandere til elbil", body: "Installation af ladeboks privat og erhverv, også med tilskud." },
  { title: "Varmepumper", body: "Rådgivning, dimensionering og installation af luft-til-vand & luft-til-luft." },
  { title: "Energioptimering", body: "Lavere elregning gennem målrettet gennemgang og opgraderinger." },
  { title: "Fejlfinding", body: "Systematisk fejlfinding og dokumenteret el-tjek af din bolig." },
  { title: "Renovering & nybyg", body: "Komplet el-entreprise ved renovering, tilbygning og nybyg." },
];

const areas = [
  ["elektriker-koebenhavn", "København"],
  ["elektriker-frederiksberg", "Frederiksberg"],
  ["elektriker-oesterbro", "Østerbro"],
  ["elektriker-norrebro", "Nørrebro"],
  ["elektriker-hellerup", "Hellerup"],
  ["elektriker-charlottenlund", "Charlottenlund"],
  ["elektriker-gladsaxe", "Gladsaxe"],
  ["elektriker-vanloese", "Vanløse"],
  ["elektriker-valby", "Valby"],
  ["elektriker-amager", "Amager"],
  ["elektriker-indre-by", "Indre By"],
  ["elektriker-nordsjaelland", "Nordsjælland"],
] as const;

function HomePage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Autoriseret elektriker · København & omegn"
        title="Din lokale elektriker, når kvalitet og pris skal mødes"
        intro="Vi løser store og små elektrikeropgaver i hele København og Storkøbenhavn. Hurtig udrykning, faste priser og garanti på alt arbejde, privat og erhverv."
        videoUrl={heroVideoAsset.url}
        imageLabel="Hero: elektriker på arbejde"
        coverage={<CoveragePills />}
      />

      <Section>
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl">Det vi kan hjælpe dig med</h2>
            <p className="mt-3 text-muted-foreground">
              Fra akut fejlfinding til komplette el-entrepriser, vi dækker hele opgavespektret med
              autoriseret håndværk og dokumenteret kvalitet.
            </p>
          </div>
          <Link
            to="/ydelser"
            className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary"
          >
            Se alle ydelser
          </Link>
        </div>
        <FeatureGrid items={services} />
      </Section>

      <Section className="!pt-0">
        <div className="grid gap-10 rounded-3xl border border-border bg-secondary/40 p-8 md:grid-cols-2 md:items-center md:p-12">
          <div>
            <span className="inline-flex w-fit items-center rounded-full bg-background px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--ink)]">
              Om Elektriker-Elektriker
            </span>
            <h2 className="mt-4 text-3xl">Lokale håndværkere, moderne metoder</h2>
            <p className="mt-4 text-muted-foreground">
              Vi er en autoriseret elinstallatørvirksomhed med base på Østerbro. Vi tror på godt
              håndværk, ærlig rådgivning og at kunden altid skal vide præcis hvad opgaven koster,
              inden vi går i gang.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[var(--ink)]/80">
              <li>✓ Autoriseret elinstallatør (CVR 35209468)</li>
              <li>✓ Gazelle-virksomhed 2017, kåret af Børsen</li>
              <li>✓ Højeste kreditværdighed (AAA), solid økonomi</li>
              <li>✓ Medlem af TEKNIQ, TEKNIQ Garanti op til 150.000 kr.</li>
              <li>✓ Faste priser og skriftligt tilbud</li>
              <li>✓ Garanti på arbejdet</li>
              <li>✓ Bæredygtig drift og el-bil flåde</li>
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/om-os" className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">
                Læs mere om os
              </Link>
              <Link to="/baeredygtighed" className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">
                Bæredygtighed
              </Link>
            </div>
          </div>
          <img
            src={photo53.url}
            alt="SAK-Electric autoriseret elektriker ved servicebil"
            loading="lazy"
            className="aspect-[5/4] w-full rounded-2xl object-cover shadow-[var(--shadow-soft)]"
          />
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl">Vores arbejde, i detaljer</h2>
          <p className="mt-3 text-muted-foreground">
            Et indblik i hverdagen hos Elektriker-Elektriker: fra montering af loftslamper til
            stikkontakter og fejlfinding bag væggen.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 md:gap-12 md:items-start">
          <div className="space-y-6">
            <img
              src={photo53.url}
              alt="Autoriseret elektriker ved servicebil i København"
              loading="lazy"
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[var(--shadow-soft)]"
            />
            <img
              src={photo50.url}
              alt="Installation af ABB ladestander til elbil"
              loading="lazy"
              className="aspect-[16/9] w-full rounded-2xl object-cover shadow-[var(--shadow-soft)]"
            />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-[var(--ink)]">Hvad vi laver, dag for dag</h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Ingen opgave er for lille eller for stor. Vi rykker ud til alt fra en enkelt
              stikkontakt i lejligheden til komplette el-installationer i erhvervslokaler.
              Her er et udpluk af det, vi hjælper vores kunder med i København og omegn:
            </p>
            <ul className="mt-6 space-y-5">
              {workItems.map((it) => (
                <li key={it.title} className="flex gap-4">
                  <span aria-hidden className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  <div>
                    <div className="font-semibold text-[var(--ink)]">{it.title}</div>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{it.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link to="/ydelser" className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground hover:brightness-95">
                Se alle vores ydelser
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl">Vi er din lokale elektriker</h2>
          <p className="mt-3 text-muted-foreground">
            Vi har biler i hele Storkøbenhavn og kan ofte være hos dig samme dag.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {areas.map(([slug, label]) => (
            <Link
              key={slug}
              to={`/${slug}` as string}
              className="group flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium text-[var(--ink)] transition hover:border-primary/50 hover:bg-accent"
            >
              <span>{label}</span>
              <span aria-hidden className="text-muted-foreground transition group-hover:translate-x-0.5">→</span>
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/sitemap" className="text-sm font-semibold text-[var(--ink)] underline-offset-4 hover:underline">
            Se alle områder vi dækker →
          </Link>
        </div>
      </Section>

      <ReviewsSection />

      <CTA />
      <div className="h-16" />
    </SiteShell>
  );
}
